import React, { useCallback, useEffect, useState } from "react"
import { Alert, Card, CardBody, Col, Label, Row, Spinner } from "reactstrap"
import FileUpload from "components/Common/FileUpload"
import { get, patch } from "helpers/api_helper"
import { EXPORT_BANNER_PATH, describeChange, exportBannerSrc, readExportBanner } from "./exportBanner"

// The image in the footer of the Excel main-draw export. A card of its own under the partner
// editor, because the two are easy to confuse and are not the same thing: the band above is
// composed from the partners; the export takes one wide image, uploaded here.
//
// One gesture: drop a file and it is in use. There is nothing else to fill in, and a wrong drop is
// undone by dropping the right file. The API stamps who and when from the session.

// The write goes through the message broker, like the partners: re-read after a beat rather than
// trust the response alone.
const REFETCH_DELAY_MS = 1500

const hintStyle = { fontSize: 11 }

const ExportBannerCard = () => {
  // null while loading; { banner, path } once read.
  const [state, setState] = useState(null)
  const [loadError, setLoadError] = useState("")
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [previewBroken, setPreviewBroken] = useState(false)

  const load = useCallback(async () => {
    try {
      const response = await get("/settings-export-banner/admin")
      setState(readExportBanner(response))
      setPreviewBroken(false)
      setLoadError("")
    } catch (error) {
      setState({ banner: null, path: "" })
      setLoadError("Failed to load the export banner")
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const save = async filename => {
    if (!filename) return
    setSaving(true)
    setMessage(null)
    try {
      await patch("/settings/exportBanner", { data: { filename } })
      setMessage({
        color: "success",
        text: "Export banner updated. The next draw export uses it — no restart needed.",
      })
      setTimeout(load, REFETCH_DELAY_MS)
    } catch (error) {
      setMessage({
        color: "danger",
        text: error?.response?.data?.message || "The image was uploaded but could not be set as the export banner.",
      })
    }
    setSaving(false)
  }

  const banner = state?.banner || null
  const src = exportBannerSrc(state?.path)

  return (
    <Card className="mb-3">
      <CardBody>
        <h6 className="mb-1">Draw export banner</h6>
        <p className="text-muted" style={{ fontSize: 13 }}>
          The Excel main-draw export (Competitions → Draw → Export) carries a{" "}
          <strong>separate uploaded image</strong> in its footer, not the band above. Changing
          partners here does not change it.
        </p>

        {loadError && <Alert color="danger">{loadError}</Alert>}
        {message && <Alert color={message.color}>{message.text}</Alert>}

        {state === null ? (
          <Spinner size="sm" animation="grow" variant="dark" />
        ) : (
          <Row>
            <Col md={7}>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e4e8eb",
                  borderRadius: 6,
                  padding: 12,
                  minHeight: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {src && !previewBroken ? (
                  <img
                    src={src}
                    alt="Current draw export banner"
                    style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain" }}
                    onError={() => setPreviewBroken(true)}
                  />
                ) : (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {src ? "The current image could not be loaded from storage." : "No image available."}
                  </span>
                )}
              </div>
              <div className="mt-2" style={{ fontSize: 12 }}>
                {banner ? (
                  describeChange(banner)
                ) : (
                  <span className="text-muted">
                    No image uploaded yet — exports use the built-in sponsors.png.
                  </span>
                )}
              </div>
            </Col>

            <Col md={5}>
              <Label style={{ fontSize: 12, fontWeight: 600 }}>
                Upload new {saving && <Spinner size="sm" className="ml-2" />}
              </Label>
              {/* Remounts once a new banner is stored, so the dropzone goes back to empty and the
                  preview on the left is the one place that shows what is in use. */}
              <FileUpload key={banner?.filename || "none"} url={EXPORT_BANNER_PATH} onChange={save} />
              <p className="text-muted mt-2 mb-0" style={hintStyle}>
                Wide PNG or JPEG — it fills a strip 13 columns by 7 rows at the bottom of the sheet.
                Dropping a file puts it in use straight away; the previous image is not kept.
              </p>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  )
}

export default ExportBannerCard
